/**
 * OAuth 2.0 Authorization Code + PKCE against the Cognito Hosted UI, web
 * edition of `app/src/auth/socialAuth.ts`. Uses `oauth4webapi` for PKCE and
 * response validation — no hand-rolled crypto (web/docs/data-layer.md §Auth).
 *
 * Flow: `beginSocialSignIn` stores the verifier/state in `sessionStorage` and
 * redirects to the authorize endpoint; Cognito redirects back to
 * `/web/auth/callback` (registered in `infra/lib/auth-stack.ts`), where
 * `completeSocialSignIn` validates the response and exchanges the code.
 */

import * as oauth from 'oauth4webapi';

import { AUTH_DOMAIN, COGNITO_CLIENT_ID } from '@/api/config';
import { decodeJwtPayload } from '@/api/auth/emailAuth';

import type { AuthProviderKind, Session } from '@/auth/types';

export type SocialProvider = 'Google' | 'SignInWithApple';

const PENDING_KEY = 'screenatlas.oauth-pending.v1';
const AUTHORIZE_ENDPOINT = `${AUTH_DOMAIN}/oauth2/authorize`;
const TOKEN_ENDPOINT = `${AUTH_DOMAIN}/oauth2/token`;

interface PendingAuth {
  codeVerifier: string;
  state: string;
  provider: SocialProvider;
  returnTo: string;
}

export interface SocialSignInResult {
  session: Session;
  /** In-app path the user came from (`/` when unknown). */
  returnTo: string;
}

// No discovery request: the OAuth2 endpoints are fixed by the Cognito custom
// domain, and hardcoding them keeps sign-in to one round-trip. `issuer` here is
// the custom-domain origin only so `validateAuthResponse` (state / error-param
// check on the redirect URL — no network) is satisfied.
//
// We deliberately do NOT run `processAuthorizationCodeResponse`: it would
// validate the Cognito id_token, whose real `iss` is
// `https://cognito-idp.<region>.amazonaws.com/<poolId>` and whose JWKS lives
// there (the custom domain serves no `.well-known/jwks.json`) — a host the
// strict `/web/*` CSP `connect-src` does not allow. The id_token is used only
// for display claims (decoded, unverified — same as the mobile app); the
// access token is the security boundary and the backend verifies it via JWKS.
const authorizationServer: oauth.AuthorizationServer = {
  issuer: AUTH_DOMAIN,
  authorization_endpoint: AUTHORIZE_ENDPOINT,
  token_endpoint: TOKEN_ENDPOINT,
};

interface CognitoTokenResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  error?: string;
  error_description?: string;
}

const client: oauth.Client = { client_id: COGNITO_CLIENT_ID };

/** Registered with Cognito — see web/docs/infra.md §Cognito. */
export function socialRedirectUri(): string {
  return `${window.location.origin}/web/auth/callback`;
}

function savePending(pending: PendingAuth): void {
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable — the callback will not find the verifier and the
    // flow degrades to a redirect back to /login.
  }
}

function takePending(): PendingAuth | null {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    if (raw) window.sessionStorage.removeItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingAuth;
    if (!parsed?.codeVerifier || !parsed?.state || !parsed?.provider) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Start the Hosted-UI redirect for `provider`. `returnTo` is the in-app path
 * to land on after a successful sign-in (the `?next` value from the guard).
 */
export async function beginSocialSignIn(provider: SocialProvider, returnTo = '/'): Promise<void> {
  const codeVerifier = oauth.generateRandomCodeVerifier();
  const codeChallenge = await oauth.calculatePKCECodeChallenge(codeVerifier);
  const state = oauth.generateRandomState();
  savePending({ codeVerifier, state, provider, returnTo });

  const url = new URL(AUTHORIZE_ENDPOINT);
  url.searchParams.set('client_id', client.client_id);
  url.searchParams.set('redirect_uri', socialRedirectUri());
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('code_challenge', codeChallenge);
  url.searchParams.set('code_challenge_method', 'S256');
  url.searchParams.set('state', state);
  url.searchParams.set('identity_provider', provider);
  window.location.assign(url.href);
}

// One exchange per page load: the callback route mounts twice under
// StrictMode, and the authorization code is single-use.
let completion: Promise<SocialSignInResult | null> | null = null;

/**
 * Finish the redirect on `/web/auth/callback`. Returns `null` when there is
 * no pending flow or the response does not validate (user cancelled, stale or
 * forged callback) — the caller should send the user back to `/login`.
 */
export function completeSocialSignIn(url: URL): Promise<SocialSignInResult | null> {
  completion ??= exchange(url);
  return completion;
}

async function exchange(url: URL): Promise<SocialSignInResult | null> {
  const pending = takePending();
  if (!pending) return null;

  let params: URLSearchParams;
  try {
    params = oauth.validateAuthResponse(authorizationServer, client, url, pending.state);
  } catch {
    return null;
  }

  const response = await oauth.authorizationCodeGrantRequest(
    authorizationServer,
    client,
    oauth.None(),
    params,
    socialRedirectUri(),
    pending.codeVerifier,
  );

  const tokens = (await response.json().catch(() => null)) as CognitoTokenResponse | null;
  if (!response.ok || !tokens?.access_token) return null;

  const claims = tokens.id_token ? decodeJwtPayload(tokens.id_token) : null;
  const provider: AuthProviderKind = pending.provider === 'Google' ? 'google' : 'apple';

  return {
    session: {
      accessToken: tokens.access_token,
      idToken: tokens.id_token,
      refreshToken: tokens.refresh_token,
      expiresAt: Date.now() + (tokens.expires_in ?? 3600) * 1000,
      email: typeof claims?.email === 'string' ? claims.email : undefined,
      name: typeof claims?.name === 'string' ? claims.name : undefined,
      sub: typeof claims?.sub === 'string' ? claims.sub : undefined,
      provider,
    },
    returnTo: pending.returnTo || '/',
  };
}
